import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '../services/api';

// Types
export interface Role {
  id: number;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserWithRoles {
  userId: number;
  userPrincipalName: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  createdAt: string | null;  // User account creation date (ticket #810)
  lastLoginAt: string | null;
  roles: Role[];
}

export interface UsersResponse {
  users: UserWithRoles[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

export type LastLoginTimeRange = 'hour' | 'day' | 'week' | 'month' | '90days' | 'year';

interface FetchUsersParams {
  page: number;
  pageSize: number;
  search?: string;
  externalOnly?: boolean;
  roleIds?: number[];
  excludeRoleIds?: number[];
  lastLoginWithin?: LastLoginTimeRange;
}

interface AssignRoleParams {
  userId: number;
  roleId: number;
}

interface RemoveRoleParams {
  userId: number;
  roleId: number;
}

// API Functions
const fetchUsers = async (params: FetchUsersParams): Promise<UsersResponse> => {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.pageSize.toString(),
  });

  if (params.search) {
    queryParams.set('search', params.search);
  }
  if (params.externalOnly) {
    queryParams.set('externalOnly', 'true');
  }
  if (params.roleIds && params.roleIds.length > 0) {
    queryParams.set('roleIds', params.roleIds.join(','));
  }
  if (params.excludeRoleIds && params.excludeRoleIds.length > 0) {
    queryParams.set('excludeRoleIds', params.excludeRoleIds.join(','));
  }
  if (params.lastLoginWithin) {
    queryParams.set('lastLoginWithin', params.lastLoginWithin);
  }

  return api.get<UsersResponse>(`/role-management/users?${queryParams}`);
};

const fetchRoles = async (): Promise<Role[]> => {
  const data = await api.get<{ roles: Role[] }>('/role-management/roles');
  return data.roles;
};

const assignRole = async ({ userId, roleId }: AssignRoleParams): Promise<void> => {
  await api.post<void>(`/role-management/users/${userId}/roles`, { roleId });
};

const removeRole = async ({ userId, roleId }: RemoveRoleParams): Promise<void> => {
  await api.delete<void>(`/role-management/users/${userId}/roles/${roleId}`);
};

// React Query Hooks

/**
 * Fetch paginated users with their roles
 */
export const useUsers = (params: FetchUsersParams, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['role-management', 'users', params],
    queryFn: () => fetchUsers(params),
    staleTime: 30000, // 30 seconds
    placeholderData: keepPreviousData, // Keep showing old data while fetching new data
    enabled: options?.enabled ?? true,
  });
};

/**
 * Fetch all available roles
 */
export const useRoles = () => {
  return useQuery({
    queryKey: ['role-management', 'roles'],
    queryFn: fetchRoles,
    staleTime: 5 * 60 * 1000, // 5 minutes (roles change infrequently)
  });
};

/**
 * Assign a role to a user
 */
export const useAssignRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignRole,
    onSuccess: () => {
      // Invalidate users query to refetch with updated roles
      queryClient.invalidateQueries({ queryKey: ['role-management', 'users'] });
    },
  });
};

/**
 * Remove a role from a user
 */
export const useRemoveRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeRole,
    onSuccess: () => {
      // Invalidate users query to refetch with updated roles
      queryClient.invalidateQueries({ queryKey: ['role-management', 'users'] });
    },
  });
};
